import { router } from "@inertiajs/react";
import { FileText, Receipt, FileCheck } from "lucide-react";
import { ListTemplate } from "@/templates/ListTemplate";
import { Table } from "@/components/Table";
import { Badge } from "@/components/Badge";
import { EmptyState } from "@/components/EmptyState";

interface StatementRow {
  id: string;
  kind: "statement" | "payout";
  label: string;
  scopeName: string;
  startDate: string | null;
  endDate: string | null;
  amountMinor: number;
  viewUrl: string;
  pdfUrl: string;
}

interface Props {
  statements: StatementRow[];
}

function fmtMoney(minor: number) {
  return `£${(minor / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtPeriod(start: string | null, end: string | null) {
  if (!start && !end) return "—";
  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
  const s = start ? new Date(start).toLocaleDateString(undefined, opts) : "—";
  const e = end ? new Date(end).toLocaleDateString(undefined, opts) : "—";
  return `${s} – ${e}`;
}

export default function StatementsIndex({ statements }: Props) {
  return (
    <ListTemplate
      title="Statements"
      description="Payout statements and settlement documents for your projects."
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Account", to: "/account" }, { label: "Statements" }]}
    >
      {statements.length === 0 ? (
        <EmptyState
          icon={<FileText />}
          title="No statements yet"
          description="Payout statements will appear here once they've been issued."
        />
      ) : (
        <Table>
          <Table.Header>
            <tr>
              <Table.Head className="pl-4">Statement</Table.Head>
              <Table.Head>Period</Table.Head>
              <Table.Head className="text-right">Amount</Table.Head>
              <Table.Head className="w-20 text-right pr-4">PDF</Table.Head>
            </tr>
          </Table.Header>
          <Table.Body>
            {statements.map((s) => (
              <Table.Row
                key={s.id}
                className="cursor-pointer"
                onClick={() => router.visit(s.viewUrl)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") router.visit(s.viewUrl);
                }}
              >
                <Table.Cell className="pl-4">
                  <div className="flex items-center gap-2.5">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-accent-subtle text-accent-text [&_svg]:size-4">
                      {s.kind === "statement" ? <FileCheck /> : <Receipt />}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-fg">{s.scopeName}</div>
                      <div className="truncate text-xs text-fg-muted">{s.label}</div>
                    </div>
                    <Badge tone={s.kind === "statement" ? "accent" : "neutral"}>
                      {s.kind === "statement" ? "Statement" : "Payout"}
                    </Badge>
                  </div>
                </Table.Cell>
                <Table.Cell className="text-fg-muted tabular whitespace-nowrap">
                  {fmtPeriod(s.startDate, s.endDate)}
                </Table.Cell>
                <Table.Cell className="text-right tabular font-medium text-fg">
                  {fmtMoney(s.amountMinor)}
                </Table.Cell>
                <Table.Cell className="text-right pr-4" onClick={(e) => e.stopPropagation()}>
                  <a
                    href={s.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Download PDF"
                    className="inline-flex size-7 items-center justify-center rounded-[var(--radius-sm)] text-fg-muted hover:bg-surface-hover hover:text-fg transition-colors [&_svg]:size-4"
                  >
                    <FileText />
                  </a>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </ListTemplate>
  );
}
